import baseApi from './RootBaseApi';
import {
  IAdvertiseData,
  IAdvertiseListResponse,
  IAdvertiseQueryParams,
  ISlotListResponse,
  AdvertiseResponse,
  SlotOption,
} from '@/types/advertise-management.types';

export const AdvertiseManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdvertises: builder.query<IAdvertiseListResponse, IAdvertiseQueryParams>(
      {
        query: (params) => ({
          url: '/advertisements/campaigns',
          method: 'GET',
          params,
        }),
        providesTags: ['Advertises'],
      }
    ),

    getAdvertiseById: builder.query<IAdvertiseData, string>({
      query: (id) => ({
        url: `/advertisements/campaigns/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: AdvertiseResponse): IAdvertiseData => {
        type CampaignData = AdvertiseResponse['data'] & {
          ad_slots?: Array<SlotOption & { placement?: string }>;
          ad_name?: string;
          image?: string;
        };
        const d = response.data as CampaignData;
        const slots: SlotOption[] | undefined = d.slots
          ? d.slots
          : d.ad_slots
            ? d.ad_slots.map((s) => ({
                id: s.id,
                name: s.name,
                position: s.position,
              }))
            : undefined;
        const slotIds: string[] | undefined = d.slot_ids
          ? d.slot_ids
          : d.ad_slots
            ? d.ad_slots.map((s) => s.id)
            : undefined;
        return {
          id: d.id,
          ad_name: d.ad_name ?? d.name,
          name: d.name,
          description: d.description ?? null,
          slot_id: d.slot_id,
          slot_ids: slotIds,
          start_date: d.start_date,
          end_date: d.end_date,
          image: d.image ?? d.image_url ?? null,
          image_url: d.image_url,
          image_captions: d.image_captions,
          target_url: d.target_url,
          status: d.status,
          impressions: d.impressions,
          clicks: d.clicks,
          ad_slots: d.ad_slots,
          slot: d.slot,
          slots,
          created_at: d.created_at,
          updated_at: d.updated_at,
        } as IAdvertiseData;
      },
      providesTags: (result, error, id) => [{ type: 'Advertises', id }],
    }),

    getSlots: builder.query<ISlotListResponse, void>({
      query: () => ({
        url: '/advertisements/ad-slots',
        method: 'GET',
      }),
      providesTags: ['Slots'],
    }),

    createAdvertise: builder.mutation<IAdvertiseData, FormData>({
      query: (formData) => ({
        url: '/advertisements/campaigns',
        method: 'POST',
        data: formData,
      }),
      invalidatesTags: ['Advertises'],
    }),

    updateAdvertise: builder.mutation<
      IAdvertiseData,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/advertisements/campaigns/${id}`,
        method: 'PUT',
        data: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Advertises', id },
        'Advertises',
      ],
    }),

    deleteAdvertise: builder.mutation<void, string>({
      query: (id) => ({
        url: `/advertisements/campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Advertises'],
    }),

    updateAdvertiseStatus: builder.mutation<
      IAdvertiseData,
      { id: string; status: number }
    >({
      query: ({ id, status }) => ({
        url: `/advertisements/campaigns/${id}/status`,
        method: 'PATCH',
        data: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Advertises', id },
        'Advertises',
      ],
    }),
  }),
});

export const {
  useGetAdvertisesQuery,
  useGetAdvertiseByIdQuery,
  useGetSlotsQuery,
  useCreateAdvertiseMutation,
  useUpdateAdvertiseMutation,
  useDeleteAdvertiseMutation,
  useUpdateAdvertiseStatusMutation,
} = AdvertiseManagementService;
